//@ts-nocheck
import { routingMixin } from "@jack-henry/web-component-router";
import { LitElement, css, html, PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { GlobalConstants, GlobalVariables } from "../../utils/constants";
import { Apis } from "../../utils/api-constants";
import {
  AuditDashboardTableData,
} from "../../types/AuditDashboard/AuditDashboardType";
import "@banno/jha-wc/src/buttons/jha-button/jha-button";
import "@banno/jha-wc/src/forms/jha-form-input-base/jha-form-input";
import "@banno/jha-wc/src/icons/jha-icon-filter";
import "@banno/jha-wc/src/collapsible-content/jha-collapsible-section/jha-collapsible-section";
import "@banno/jha-wc/src/forms/jha-form-date-input/jha-form-date-input";
import "@banno/jha-wc/src/tables/jha-table-row/jha-table-row";
import "@banno/jha-wc/src/tables/jha-table-header/jha-table-header";
import "@banno/jha-wc/src/tables/jha-table-cell/jha-table-cell";
import "@banno/jha-wc/src/icons/jha-icon-circle-checkmark-filled";
import "@banno/jha-wc/src/icons/jha-icon-circle-close-filled";
import "@banno/jha-wc/src/icons/jha-icon-information";
import "@banno/jha-wc/src/icons/jha-icon-arrow-back";
import "@banno/jha-wc/src/icons/jha-icon-refresh";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { showToast } from "@banno/jha-wc/src/js/utils/toast-util.js";
import { AppRouters } from "../../utils/app-router";
import "../../Components/Loader";
import { jhaDropdownMenuPanelStylesText } from "../../styles/jha-dropdown-menu-panel";
import { fixedEncodeURIComponent, getRequest, postRequest } from "../../utils/jha-fetch";
import { getStatusToLowerCase, AUDIT_STATUS_BADGE_MAP, renderAuditDetailsStatusCell, applyAuditReportingTableLayout, ADVANCED_TABLE_SORT_BUTTON_HEADER_STYLES_TEXT, STATUS_COLUMN_HEADER_ADVANCED_TABLE_STYLES_TEXT, AUDIT_DETAILS_ELLIPSIS_TITLE_COLUMN_IDS, stripAdvancedTableRedundantTitles, renderAdvancedTableEllipsisCell, buildAdvancedTableEllipsisColumnShadowCss, buildAdvancedTableJhaTooltipOverflowEscapeCss } from "../../utils/app-utils";
import {
  dateTimeColumnLabel,
  EFM_TIMEZONE_GRID_TABLE_SHADOW_STYLES_TEXT,
  EFM_TIMEZONE_GRID_TABLE_STYLE,
  formatEpochSecondsForDisplay,
  mapAuditGridRowForSort,
  normalizeEpochSeconds,
  orderLoadedRowsByEpoch,
  renderGridWithTimezoneBanner,
} from "../../utils/datetime-display";
import "@banno/jha-wc/src/jha-tooltip/jha-tooltip";
import {
  AUDIT_DASHBOARD_SESSION_RESTORE_FLAG_KEY,
  clearAuditDashboardSessionStorageOnRouteExit,
} from "../../utils/audit-dashboard-session-storage";

/**
 * Audit Details: only grid with click-to-copy jha-tooltips — preserve all renderers;
 * overflow-escape CSS prevents clipped tooltips (ACME-1814).
 */
const AUDIT_DETAILS_JHA_TOOLTIP_COLUMN_IDS = [
  "logDateTime",
  "processName",
  "businessUnit",
  "sourceFileServer",
  "destinationFileServer",
  "destinationFileSize",
  "status",
] as const;

/** From audit list navigation; details API step rows often omit `workflowName`. */
function readWorkflowNameFromLocationSearch() {
  if (typeof window === "undefined") return "";
  try {
    const q = new URLSearchParams(window.location.search);
    const raw = q.get("workflowName") ?? q.get("workflow");
    if (raw == null || raw === "") return "";
    return String(raw).trim();
  } catch {
    return "";
  }
}

export const WORKFLOW_NOT_FOUND_TOAST =
  "Could not locate the workflow for this trace.";

/** Disable in-page Refresh after a click so the details API cannot be spammed. */
export const AUDIT_DETAILS_REFRESH_COOLDOWN_MS = 5000;

function readQueryParam(name: string): string {
  if (typeof window === "undefined") return "";
  try {
    const raw = new URLSearchParams(window.location.search).get(name);
    return raw == null ? "" : String(raw).trim();
  } catch {
    return "";
  }
}

function readWorkflowIdFromLocationSearch(): string {
  return readQueryParam("workflowId");
}

@customElement("audit-details-page")
export class AuditDetailsPage extends routingMixin(LitElement) {
  @property({ reflect: true, attribute: "trace-id" })
  traceId = "";
  @property()
  loading = false;
  @property({ type: Boolean })
  refreshCoolingDown = false;
  private refreshCooldownTimer: ReturnType<typeof setTimeout> | undefined;

  static get is() {
    return "audit-dashboard";
  }
  constructor() {
    super();
  }
  @property({ type: Array })
  detailColumns = [
    {
      label: dateTimeColumnLabel('LOG DATE TIME'),
      id: 'logDateTime',
      sortBy: true,
      dataType: "number",
      valueFormatter: () => "",
      valueRenderer: (val: unknown) => html`<jha-tooltip text="Click To Copy" bottom>${formatEpochSecondsForDisplay(normalizeEpochSeconds(val))}</jha-tooltip>`
    },
    {
      label: 'LOG MESSAGE',
      id: 'logMessage',
      sortBy: true,
      valueRenderer: (val) => renderAdvancedTableEllipsisCell(val, { withTitle: true }),
    },
    {
      label: "STATUS",
      id: "status",
      sortBy: true,
      dataType: 'badge',
      badgeMap: [...AUDIT_STATUS_BADGE_MAP],
      valueRenderer: (val) => renderAuditDetailsStatusCell(val),
    },
    {
      label: 'JOB NAME',
      id: 'processName',
      sortBy: true,
      valueRenderer: (val) => html`<jha-tooltip text="Click To Copy" bottom>${val}</jha-tooltip>`
    },
    {
      label: 'BUSINESS UNIT',
      id: 'businessUnit',
      sortBy: true,
      valueRenderer: (val) => html`<jha-tooltip text="Click To Copy" bottom>${val}</jha-tooltip>`
    },
    {
      label: 'SOURCE SERVER',
      id: 'sourceFileServer',
      sortBy: true,
      valueRenderer: (val) => html`<jha-tooltip text="Click To Copy" bottom>${val}</jha-tooltip>`
    },
    {
      label: 'SOURCE PATH',
      id: 'sourceFilePath',
      sortBy: true,
      valueRenderer: (val) => renderAdvancedTableEllipsisCell(val, { withTitle: true }),
    },
    {
      label: 'DESTINATION SERVER',
      id: 'destinationFileServer',
      sortBy: true,
      valueRenderer: (val) => html`<jha-tooltip text="Click To Copy" bottom><span>${val}</span></jha-tooltip>`
    },
    {
      label: 'DESTINATION PATH',
      id: 'destinationFilePath',
      sortBy: true,
      valueRenderer: (val) => renderAdvancedTableEllipsisCell(val, { withTitle: true }),
    },
    {
      label: 'FILE SIZE',
      id: 'destinationFileSize',
      sortBy: true,
      dataType: 'number',
      valueRenderer: (val) => html`<jha-tooltip text="Click To Copy" bottom>${val}</jha-tooltip>`
    },
    {
      label: 'DESTINATION FILE NAME',
      id: 'fileName',
      sortBy: true,
      valueRenderer: (val) => renderAdvancedTableEllipsisCell(val, { withTitle: true }),
    },
  ].slice();

  static readonly styles = css`
    jha-icon-circle-checkmark-filled {
      fill: var(--jha-color-success);
    }
    jha-icon-circle-close-filled {
      fill: var(--jha-color-warning);
    }
    #formCon {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    #detailsHeader {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
      padding-left: 5px;
    }
    .detailsHeaderActions {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
    }
    #detailsHeader jha-button {
      margin: 0;
      width: auto;
    }
    #detailsMeta {
      display: flex;
      flex-direction: column;
      gap: 8px;
      max-width: 100%;
      margin-top: 10px;
    }
    .detailsMetaLine {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 0.35em 0.5em;
      max-width: 100%;
      font-size: 15px;
      line-height: 1.45;
    }
    /* Match jha-table-header column labels (audit results table) */
    .detailsMetaKey {
      flex: 0 0 auto;
      font-size: var(--jha-table-header-font-size, 12px);
      font-weight: var(--jha-table-header-font-weight, 600);
      text-transform: var(--jha-table-header-transform, uppercase);
      color: var(--jha-table-header-color, var(--jha-text-light, #8c989f));
    }
    .detailsMetaInlineValue {
      flex: 1 1 12rem;
      min-width: 0;
      color: var(--jha-text-base, var(--jh-color-text-primary, #e6e6e6));
      overflow-wrap: anywhere;
      word-break: break-word;
    }
    /* Primary accent + mono: TraceID and Workflow Name values */
    .detailsMetaInlineValue.detailsMetaPrimary {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 14px;
      color: var(--jha-color-primary, #3aaeda);
    }
    .workflowNameLink {
      background: none;
      border: 0;
      padding: 0;
      margin: 0;
      cursor: pointer;
      text-align: left;
      font: inherit;
      color: inherit;
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    .workflowNameLink:hover,
    .workflowNameLink:focus-visible {
      filter: brightness(1.15);
      outline: none;
    }
    #reportingDiv {
      display: flex;
      flex-direction: column;
      padding: 24px;
      gap: 15px;
      height: 100%;
      box-sizing: border-box;
    }
    jha-form-row {
      align-items: baseline;
      justify-content: flex-end;
      flex: 1;
    }
    jha-table-header {
      width: 20vw;
    }
    jha-card {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      overflow: hidden;
      margin-left: 0px;
      margin-right: 0px;
      padding: 5px;
    }
    .efm-timezone-banner {
      flex: 0 0 auto;
      width: 100%;
    }
    #filterBody {
      display: flex;
      flex-direction: column;
    }
    #filterId {
      display: flex;
      flex-direction: row;
      align-items: baseline;
      flex: 1;
    }
    #dateDiv {
      display: flex;
      flex-direction: row;
      align-items: baseline;
      justify-content: space-between;
    }
    jha-message {
      display: flex;
      flex-direction: row;
      align-items: left;
      margin-top: 10px;
      max-width: 23%;
    }

    #table-container {
      display: flex;
      flex: 1;
      min-height: 0;
      width: 100%;
    }
  `;
  @property({ type: Object })
  auditDetailsData: AuditDashboardTableData = {
    messageStatus: "",
    results: [],
    paging: 0,
    eTags: "",
  };
  @property({ type: Object })
  auditDetailsDataFetch: AuditDashboardTableData = {
    messageStatus: "",
    results: [],
    paging: 0,
    eTags: "",
  };
  @property()
  filterTraceId = "";
  @property()
  sortColumnId = "";
  @property()
  sortDirection = "";
  @property({ type: Object })
  advancedTableConfig = {
    /** Client-side sort; details API does not accept sort params (unlike audit list). */
    apiControlled: false,
    apiTotal: 2000,
    sort: {
      columnId: "logDateTime",
      descending: false,
    },
  };
  @property({ type: Boolean })
  loadingMore = false;
  @property()
  nextPageKey = "";
  @property()
  pageLimit = 100;
  get listData() {
    const rows = (this.auditDetailsData.results ?? []).map((item) =>
      mapAuditGridRowForSort({ ...item }),
    );
    const sort = this.advancedTableConfig?.sort;
    return orderLoadedRowsByEpoch(rows, sort?.columnId, sort?.descending, ["logDateTime"]);
  }

  /** Shown in the page header; list uses query param, else first non-empty row from API. */
  get detailWorkflowId(): string {
    const fromQuery = readWorkflowIdFromLocationSearch();
    if (fromQuery) return fromQuery;
    const rows = this.auditDetailsData?.results ?? [];
    for (const row of rows) {
      const id = row?.workflowId;
      if (id == null) continue;
      const s = String(id).trim();
      if (s) return s;
    }
    return "";
  }

  /** Shown in the page header; list uses query param, else first non-empty row from API. */
  get detailWorkflowName(): string {
    const fromQuery = readWorkflowNameFromLocationSearch();
    if (fromQuery) return fromQuery;
    const rows = this.auditDetailsData?.results ?? [];
    for (const row of rows) {
      const w = row?.workflowName;
      if (w == null) continue;
      const s = String(w).trim();
      if (s) return s;
    }
    return "";
  }

  /** Shown in the page header; list uses query param, else first non-empty row from API. */
  get detailActivatedBy(): string {
    const fromQuery = readQueryParam("activatedBy");
    if (fromQuery) return fromQuery;
    const rows = this.auditDetailsData?.results ?? [];
    for (const row of rows) {
      const v = row?.activatedBy;
      if (v == null) continue;
      const s = String(v).trim();
      if (s) return s;
    }
    return "";
  }

  get workflowSourceLocationPath(): string {
    const wf = this.detailWorkflowName;
    if (!wf) return "";
    let bu = readQueryParam("businessUnit");
    let srv = readQueryParam("sourceFileServer");
    if (!bu || !srv) {
      const rows = this.auditDetailsData?.results ?? [];
      for (const row of rows) {
        const rowBu = row?.businessUnit != null ? String(row.businessUnit).trim() : "";
        const rowSrv = row?.sourceFileServer != null ? String(row.sourceFileServer).trim() : "";
        if (rowBu && rowSrv) {
          bu = bu || rowBu;
          srv = srv || rowSrv;
          break;
        }
      }
    }
    if (!bu || !srv) return "";
    return `/${srv}/${bu}/${wf}/`;
  }

  async resolveWorkflowIdFromSearch(): Promise<string> {
    const wf = this.detailWorkflowName;
    if (!wf) return "";
    let bu = readQueryParam("businessUnit");
    if (!bu) {
      const rows = this.auditDetailsData?.results ?? [];
      for (const row of rows) {
        const rowBu = row?.businessUnit != null ? String(row.businessUnit).trim() : "";
        if (rowBu && rowBu.toLowerCase() !== "default") {
          bu = rowBu;
          break;
        }
      }
    }
    if (!bu) return "";

    try {
      const response = await postRequest<{ workflowName: string; businessUnit: string }>(
        Apis.WORKFLOW_LIST,
        { workflowName: wf, businessUnit: bu },
        undefined,
        true
      );
      if (!response || (typeof Response !== "undefined" && response instanceof Response)) {
        return "";
      }
      if (response.messageStatus !== null) return "";
      const results = (response as any)?.results ?? [];
      const wfKey = wf.toLowerCase();
      const match = results.find(
        (r: any) => String(r?.workflowName ?? "").trim().toLowerCase() === wfKey
      );
      const id =
        typeof match?.workflowId === "string"
          ? match.workflowId.trim()
          : "";
      return id ? id : "";
    } catch {
      return "";
    }
  }

  async onWorkflowNameClick() {
    if (this.loading) return;
    const workflowId = this.detailWorkflowId;
    if (workflowId) {
      try {
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.setItem(AUDIT_DASHBOARD_SESSION_RESTORE_FLAG_KEY, "1");
        }
      } catch {
        /* ignore */
      }
      AppRouters.go(`/workflows/edit-workflow/${workflowId}`);
      return;
    }
    this.loading = true;
    let resolvedWorkflowId = "";
    try {
      resolvedWorkflowId = await this.resolveWorkflowIdFromSearch();
    } finally {
      this.loading = false;
    }
    if (!resolvedWorkflowId) {
      showToast({
        message: WORKFLOW_NOT_FOUND_TOAST,
        role: "danger",
        duration: GlobalVariables.TOAST_TIMEOUT,
      });
      return;
    }
    try {
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(AUDIT_DASHBOARD_SESSION_RESTORE_FLAG_KEY, "1");
      }
    } catch {
      /* ignore */
    }
    AppRouters.go(`/workflows/edit-workflow/${resolvedWorkflowId}`);
  }

  async connectedCallback() {
    super.connectedCallback();
    await this.loadAuditDetails();
  }

  disconnectedCallback() {
    if (this.refreshCooldownTimer !== undefined) {
      clearTimeout(this.refreshCooldownTimer);
      this.refreshCooldownTimer = undefined;
    }
    super.disconnectedCallback();
  }

  /** In-page Refresh click — cooldown does not apply to F5 / connectedCallback. */
  onRefreshClick() {
    if (!this.traceId || this.loading || this.refreshCoolingDown) return;
    this.refreshCoolingDown = true;
    if (this.refreshCooldownTimer !== undefined) {
      clearTimeout(this.refreshCooldownTimer);
    }
    this.refreshCooldownTimer = setTimeout(() => {
      this.refreshCoolingDown = false;
      this.refreshCooldownTimer = undefined;
    }, AUDIT_DETAILS_REFRESH_COOLDOWN_MS);
    return this.loadAuditDetails();
  }

  async loadAuditDetails() {
    if (!this.traceId) return;
    this.loading = true;
    try {
      const res = await getRequest(Apis.AUDIT_DETAILS + `/${this.traceId}`);
      if (res?.messageStatus === null) {
        this.auditDetailsData = getStatusToLowerCase(res) ?? {};
        this.nextPageKey = res?.pageKey ?? "";
        this.advancedTableConfig = {
          ...this.advancedTableConfig,
          apiTotal: res?.totalCount ?? this.advancedTableConfig.apiTotal,
        };
      } else if (res?.details?.length > 0) {
        const errMsg = getErrorMessage(res.details);
        showToast({
          message: errMsg,
          role: "danger",
          duration: GlobalVariables.TOAST_TIMEOUT,
        });
      } else {
        showToast({
          message: "Something went wrong, please try again.",
          role: "danger",
          duration: GlobalVariables.TOAST_TIMEOUT,
        });
      }
    } catch {
      showToast({
        message: "Something went wrong, please try again.",
        role: "danger",
        duration: GlobalVariables.TOAST_TIMEOUT,
      });
    } finally {
      this.loading = false;
    }
  }
  onTraceIDChange(e: { target: { value: string } }) {
    this.filterTraceId = e.target.value;
  }
  onRowClick(evt) {
    if (window.isSecureContext && navigator.clipboard) {
      navigator.clipboard.writeText(evt.detail.item[evt.detail.colId]).then(
        function () {
          showToast({
            message: `Copied To Clipboard.`,
            role: "success",
            duration: GlobalVariables.TOAST_TIMEOUT,
          });
        },
        function (err) {
          showToast({
            message: `Failed To Copy`,
            role: "danger",
            duration: GlobalVariables.TOAST_TIMEOUT,
          });
        }
      );
    }
  }
  async fetchNextPageData() {
    this.loading = true;
    try {
      const res = await getRequest(Apis.AUDIT_DETAILS + `/${this.traceId}`);
      if (res?.messageStatus === null) {
        this.auditDetailsDataFetch = getStatusToLowerCase(res) ?? {};
        this.nextPageKey = res?.pageKey ?? "";
      } else if (res?.details?.length > 0) {
        const errMsg = getErrorMessage(res.details);
        showToast({
          message: errMsg,
          role: "danger",
          duration: GlobalVariables.TOAST_TIMEOUT,
        });
      } else {
        showToast({
          message: "Something went wrong, please try again.",
          role: "danger",
          duration: GlobalVariables.TOAST_TIMEOUT,
        });
      }
    } catch {
      showToast({
        message: "Something went wrong, please try again.",
        role: "danger",
        duration: GlobalVariables.TOAST_TIMEOUT,
      });
    } finally {
      this.loading = false;
    }
  }
  async handleLoadMore() {
    if (this.nextPageKey !== "") {
      await this.fetchNextPageData();
      this.loadingMore = true;
      const newData: AuditDashboardTableData = {
        messageStatus: this.auditDetailsDataFetch.messageStatus,
        results: [...this.auditDetailsData.results, ...this.auditDetailsDataFetch.results],
        pageKey: this.auditDetailsDataFetch.pageKey,
        totalCount: this.auditDetailsDataFetch.totalCount
      }
      this.auditDetailsData = { ...newData };
      this.loadingMore = false;
    }
  }
  renderTable() {
    return html`
    <div id="table-container">
      <jha-advanced-table style=${EFM_TIMEZONE_GRID_TABLE_STYLE} .tableConfig=${this.advancedTableConfig} .columns=${this.detailColumns} .data=${this.listData}
        .fullWidthColumns=${true} .loading=${this.loading} .noContentMessage=${"No Data to Display"}
        @pagination-forward=${this.handleLoadMore} @table-cell-click=${this.onRowClick} @table-update=${this.handleTableUpdate} .loadingMore=${this.loadingMore}
        .pageLimit=${this.pageLimit}></jha-advanced-table>
    </div>
    `;
  }

  updated(changed: PropertyValues) {
    super.updated?.(changed);
    const table = this.renderRoot.querySelector("jha-advanced-table") as HTMLElement | null;
    if (table) {
      table.toggleAttribute("data-api-loading", this.loading || this.loadingMore);
      table.toggleAttribute(
        "data-has-data",
        (this.auditDetailsData?.results?.length ?? 0) > 0,
      );
    }
    requestAnimationFrame(() => {
      this.injectAdvancedTableStyles();
      this.stripRedundantTableTitles();
    });
  }

  private handleTableUpdate() {
    requestAnimationFrame(() => {
      this.injectAdvancedTableStyles();
      this.stripRedundantTableTitles();
    });
  }

  private stripRedundantTableTitles() {
    const table = this.renderRoot.querySelector("jha-advanced-table");
    stripAdvancedTableRedundantTitles(
      table?.shadowRoot,
      AUDIT_DETAILS_ELLIPSIS_TITLE_COLUMN_IDS,
    );
  }

  private injectAdvancedTableStyles() {
    const table = this.renderRoot.querySelector("jha-advanced-table") as HTMLElement | null;
    const root = table?.shadowRoot;
    if (!root) return;
    const css = `
        ${ADVANCED_TABLE_SORT_BUTTON_HEADER_STYLES_TEXT}
        ${STATUS_COLUMN_HEADER_ADVANCED_TABLE_STYLES_TEXT}
        ${EFM_TIMEZONE_GRID_TABLE_SHADOW_STYLES_TEXT}
        ${jhaDropdownMenuPanelStylesText}
        .loading jh-progress { visibility: hidden; }
        :host([data-api-loading]) .no-content,
        :host([data-has-data]) .no-content { visibility: hidden; }
        .no-content p {
            font-size: 20px;
            font-weight: 600;
            margin: 0;
        }
        ${buildAdvancedTableEllipsisColumnShadowCss([
      "logMessage",
      "sourceFilePath",
      "destinationFilePath",
      "fileName",
    ])}
        ${buildAdvancedTableJhaTooltipOverflowEscapeCss(AUDIT_DETAILS_JHA_TOOLTIP_COLUMN_IDS)}
    `;
    let style = root.querySelector("style[data-acme-1180]") as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement("style");
      style.setAttribute("data-acme-1180", "");
      root.appendChild(style);
    }
    style.textContent = css;
    applyAuditReportingTableLayout(root);
  }

  async routeExit(currentNode, nextNode, routeId, context) {
    clearAuditDashboardSessionStorageOnRouteExit(context);
    await super.routeExit(currentNode, nextNode, routeId, context);
  }

  onBackClick() {
    try {
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(AUDIT_DASHBOARD_SESSION_RESTORE_FLAG_KEY, "1");
      }
    } catch {
      /* ignore */
    }
    AppRouters.go("/audit-reporting");
  }
  render() {
    return html`
      ${this.loading ? html`<circle-loader></circle-loader>` : undefined}
      <div id="reportingDiv">
        <div id="formCon">
          <header id="detailsHeader">
            <div class="detailsHeaderActions">
              <jha-tooltip text="Return to audit reporting. Your list filters are kept when you go back." bottom>
                <jha-button type="button" data-auto-id="backBtn" primary large sync @click=${this.onBackClick}>
                  <jha-icon-arrow-back slot="icon-left"></jha-icon-arrow-back>
                  Back to results
                </jha-button>
              </jha-tooltip>
              <jha-tooltip text="Refresh the current trace without leaving this page." bottom>
                <jha-button
                  type="button"
                  data-auto-id="reloadDetailsBtn"
                  primary
                  large
                  sync
                  ?disabled=${this.loading || this.refreshCoolingDown}
                  @click=${() => void this.onRefreshClick()}
                >
                  <jha-icon-refresh slot="icon-left"></jha-icon-refresh>
                  Refresh
                </jha-button>
              </jha-tooltip>
            </div>
            <div id="detailsMeta">
              <div class="detailsMetaLine">
                <span class="detailsMetaKey">TraceID:</span>
                <span class="detailsMetaInlineValue detailsMetaPrimary">${this.traceId}</span>
              </div>
              <div class="detailsMetaLine">
                <span class="detailsMetaKey">Workflow Name:</span>
                ${this.detailWorkflowName
        ? html`<jha-tooltip
                      text="Open workflow details (View if active, Edit if inactive)"
                      bottom
                    >
                      <button
                        type="button"
                        class="detailsMetaInlineValue detailsMetaPrimary workflowNameLink"
                        data-auto-id="workflowNameLink"
                        @click=${this.onWorkflowNameClick}
                      >
                        ${this.detailWorkflowName}
                      </button>
                    </jha-tooltip>`
        : html`<span
                      class="detailsMetaInlineValue detailsMetaPrimary"
                      title=${undefined}
                      >—</span
                    >`}
              </div>
              <div class="detailsMetaLine">
                <span class="detailsMetaKey">Activated By:</span>
                <span
                  class="detailsMetaInlineValue detailsMetaPrimary"
                  data-auto-id="activatedByValue"
                  >${this.detailActivatedBy || "—"}</span
                >
              </div>
            </div>
          </header>
        </div>
        <jha-card
          >${renderGridWithTimezoneBanner(this.renderTable())}</jha-card
        >
      </div>
    `;
  }
}
declare global {
  interface HTMLElementTagNameMap {
    "audit-details-page": AuditDetailsPage;
  }
}